/**
 * This file contains all the helper functions required for this application
 */
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import React from "react";
import ReactDOM from 'react-dom/client';
import AlertMessage from "../components/common/Alert/AlertMessage";
import UpdationModal from "../components/common/Alert/UpdationModal";
import Extent from '@arcgis/core/geometry/Extent';
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine.js";
import DOMPurify from "dompurify";
import SpatialReference from "@arcgis/core/geometry/SpatialReference.js";
import * as projection from "@arcgis/core/geometry/projection.js";
/**--------------------------------GLOBAL VARIABLES----------------------------- */
let highlightLayer = new GraphicsLayer({ id: 'highlightsLayer' });
/**----------------------------------END---------------------------------------- */


/**
 * Method to get graphic symbol based on geometry type
 * @param {string} type 
 * @returns {Object} - The symbol to be used for the given geometry type.
 */
const getSymbolByGeometryType = (type) => {
    switch (type) {
        case 'point':
            return window.graphicSymbols.pointSymbol;
        case 'polygon':
            return window.graphicSymbols.polygonSymbol;
        case 'polyline':
            return window.graphicSymbols.polylineSymbol;
        default:
            return null;
    }
};

/**
 * Adds the given graphics to the layer
 * @param {Object} layer - The layer to which the graphics will be added.
 * @param {Array<Object>} graphicArray - An array of graphics to be added to the layer.
 * @returns {void} - No return value.
 */
export const addGraphicsToLayer = (layer, graphicArray) => {
    graphicArray.forEach(graphic => {
        var graphicGeometry = graphic.geometry;
        let newGraphic = new Graphic({
            geometry: graphicGeometry,
            symbol: getSymbolByGeometryType(graphicGeometry.type),
        });
        layer.graphics.add(newGraphic);
    });
}

export const addGraphicsToMap = (mapview, graphicArray) => {
    graphicArray.forEach(graphic => {
        var graphicGeometry = graphic.geometry;
        let newGraphic = new Graphic({
            geometry: graphicGeometry,
            symbol: getSymbolByGeometryType(graphicGeometry.type),
        });
        mapview.graphics.add(newGraphic);
    });
}

/**
 * Normalizes the keys of an object to lowercase.
 * @param {Object} obj - The original object.
 * @returns {Object} - A new object with lowercase keys.
 */
export const normalizeKeys = (obj) => {
    return Object.keys(obj).reduce((acc, key) => {
        acc[key.toLowerCase()] = obj[key];
        return acc;
    }, {});
};

/**
 * Accesses a key in an object with case insensitivity.
 * @param {Object} obj - The original object.
 * @param {string} key - The key to look up.
 * @returns {*} - The value associated with the case-insensitive key, or undefined if not found.
 */
export const getValueCaseInsensitive = (obj, key) => {
    const normalizedObj = normalizeKeys(obj);
    return normalizedObj[key.toLowerCase()];
};

/**
 * Method to highlight and zoom to given features using layer view
 * @param {*} features 
 * @param {*} mapview 
 * @param {*} highlightOptions 
 * @param {*} zoomLevel  (optional)
 */
export const highlightAndZoomToFeatureUsingLayerView = (features, mapview, highlightOptions, zoomLevel) => {
    // Remove any previous highlights
    mapview.graphics.removeAll()
    highlightLayer.graphics.removeAll();

    // Create highlight graphics for each feature and add to the highlight layer
    let highlightGraphics = features.map((feature) => {
        let newGraphic = null;
        if (feature.geometry) {
            newGraphic = new Graphic({
                geometry: feature.geometry,
                symbol: getSymbolByGeometryType(feature.geometry.type),
            });
        }

        return newGraphic;
    });

    highlightLayer.graphics.addMany(highlightGraphics);
    mapview.map.add(highlightLayer);

    // Calculate the combined extent for all features
    let extents = features.map(feature => feature.geometry?.extent).filter(extent => extent !== null);
    if(features.length === 1 && features[0].geometry?.type === 'point') {
        let point = features[0].geometry;
        if (point.spatialReference.isGeographic) {
            point = projection.project(point, { wkid: 3857 }); // Web Mercator
          }
        
          const buffer = geometryEngine.buffer(point, 200, "meters");
          let pointExtent = buffer.extent.expand(1.5);
          if(pointExtent){
            extents.push(pointExtent);
          }

    }else{
         // Handle geometries without extent, e.g., points
    features.forEach(feature => {
        if (feature.geometry?.type === 'point') {
            let point = feature.geometry;
          let pointExtent = new Extent({
            xmin: point.x - 1,
            ymin: point.y - 1,
            xmax: point.x + 1,
            ymax: point.y + 1,
            spatialReference: point.spatialReference
        })
           
              if(pointExtent){
                extents.push(pointExtent);
              }
           
        }
    });

    }
   

    // Combine all extents
    if (extents.length > 0) {
        extents = extents.filter(extent => extent !== null && extent !== undefined);
        const combinedExtent = extents.reduce((acc, curr) => geometryEngine.union(acc, curr), extents[0]);

     // This zoom level is set for the Identity widget. When the user clicks on any attachment, the corresponding feature will be highlighted at a specific zoom level. Otherwise, the else block will function normally without applying any zoom level.
        if (zoomLevel) {
            mapview.goTo(combinedExtent).then(() => {
                // Adjust zoom level (positive = zoom out, negative = zoom in)
                mapview.zoom = zoomLevel;
            }).catch(error => {
                console.error('Error zooming to combined extent:', error);
            });
        }
        else {
            mapview.goTo(combinedExtent).catch(error => {
                console.error('Error zooming to combined extent:', error);
            });
        }
        // Zoom to the combined extent

    } else {
        console.warn('No valid extents available for zooming');
    }
};

/**
 * Method to remove all the highlights added to the map 
 */
export function removeAllHighlights() {
    if (highlightLayer && highlightLayer.graphics) {
        highlightLayer.graphics.removeAll();
    }
}

/**
 * example 1234567 => 12,34,567 (en-IN)
 * example 1234567 => 1,234,567 (en-US)
 * @param {*} inputNumber 
 * @returns formattedNumber
 */
export function formatNumber(inputNumber) {
    const _formatter = (number, locale = 'en-US', options = {}) => {
        return new Intl.NumberFormat(locale, options).format(number);
    }
    const formattedNumber = _formatter(inputNumber, 'en-US', { style: 'decimal' });
    return formattedNumber;
}

export const convertDegreeToDMS = (degree) => {
    return [0 | degree, '° ', 0 | (degree < 0 ? degree = -degree : degree) % 1 * 60, "' ", 0 | degree * 60 % 1 * 60, '"'].join('');
}

export const removeMapGraphics = (mapview) => {
    if (mapview) {
        mapview.graphics?.removeAll();
    }
}

export const showAlert = (message, type, html = false) => {
    const container = document.createElement('div');
    const sanitizedMessage = html ? DOMPurify.sanitize(message, { ALLOWED_TAGS: ["b", "i", "strong", "em", "br"] }) : DOMPurify.sanitize(message);
    const sanitizedType = DOMPurify.sanitize(type);
    document.body.appendChild(container);

    const handleClose = () => {
        root.unmount(container);
        container.remove();
    };

    const alert = (
        <AlertMessage message={{ text: sanitizedMessage, type: sanitizedType }} closeMessage={handleClose} html={html} />
    );

    const root = ReactDOM.createRoot(container);
    root.render(alert);
};

export const showUpdationModal = (title, type, navigate, navigateTo) => {
    const container = document.createElement('div');
    // const sanitizedTitle = DOMPurify.sanitize(title);
    const sanitizedTitle = title;
    const sanitizedType = DOMPurify.sanitize(type);
    document.body.appendChild(container);

    const handleClose = () => {
        root.unmount(container);
        container.remove();
    };

    const modal = (
        <UpdationModal message={{ title: sanitizedTitle }} type={sanitizedType} onClose={handleClose} navigate={navigate} navigateTo={navigateTo} />
    );

    const root = ReactDOM.createRoot(container);
    root.render(modal);
};


export const getPropertyValue = (array, key, keyValue, property) => {
    const foundItem = array.find(item => item[key] === keyValue);
    return foundItem ? foundItem[property] : null;
}

/**This method is created to update the map cursor tooltip text during draw/sketch
 * adjust the tooltip text as per the sketch state
 */
export const updateTooltipText = (event, sketchTool, tooltipRef, t) => {
    if (!event || !sketchTool || !tooltipRef || !tooltipRef.current) return;

    if (event.tool === "point") {
        tooltipRef.current.innerHTML = t('clickToAddPoint');
        tooltipRef.current.classList.add('mapTooltip');
        return;
    }
    if (event.toolEventInfo && event.toolEventInfo.type === "vertex-add") {

        tooltipRef.current.innerHTML = t('clickToContinue');
        tooltipRef.current.classList.add('mapTooltip');
        if (event.tool === "polyline") {
            let vertices = event.graphic.geometry.paths[0].length;
            if (vertices && vertices > 1) {
                tooltipRef.current.innerHTML = t('doubleClickToFinish');
                tooltipRef.current.classList.add('mapTooltip');
                return;
            }
        }
        if (event.tool === "polygon") {
            let vertices = event.graphic.geometry.rings[0].length;
            if (vertices && vertices > 2) {
                tooltipRef.current.innerHTML = t('doubleClickToFinish');
                tooltipRef.current.classList.add('mapTooltip');
                return;
            }
        }


    } else if (event.state === "ready" || event.state === "start") {

        tooltipRef.current.innerHTML = t('clickOnMapToStartDrawing');
        tooltipRef.current.classList.add('mapTooltip');

    } else if (event.state === "complete") {

        if (sketchTool.state === "ready" && sketchTool.creationMode === "continuous") {
            tooltipRef.current.innerHTML = t('clickOnMapToStartDrawing');
            tooltipRef.current.classList.add('mapTooltip');
        }

    } else if (event.state === "cancel") {

        if (sketchTool.state === "ready" && sketchTool.creationMode === "continuous") {
            tooltipRef.current.innerHTML = t('clickOnMapToStartDrawing');
            tooltipRef.current.classList.add('mapTooltip');
        }

    }
}